import { Button, Label, Spinner, Textarea } from 'flowbite-react';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Dropdown, validateEmailAddress } from 'shared-utils';
import { bulkAction } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { BulkActionByRecruiterInput, UIUserRecommendation } from 'types';

const RecruiterShareCandidates = ({
	positionId,
	bulkSelectedCandidates,
	onSuccess,
}: {
	positionId?: string;
	bulkSelectedCandidates: UIUserRecommendation[];
	onSuccess?: () => void;
}) => {
	const toast = useToast();
	const [inputValue, setInputValue] = React.useState('');
	const [value, setValue] = React.useState<MultiValue<Dropdown>>([]);
	const [emailErrorMessage, setEmailErrorMessage] = React.useState('');

	const [formDetails, setFormDetails] = useState<BulkActionByRecruiterInput>({
		action: 'share-profile',
		userIds: [],
		positionId: '',
	});

	const bulkActionFn = useMutation(['bulkAction'], bulkAction, {
		onSuccess() {
			toast({
				title: 'Email sent successfully',
				type: 'success',
			});
			onSuccess && onSuccess();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const handleInputChange = (newValue: string) => {
		if (!newValue) return;
		switch (newValue[newValue.length - 1]) {
			case ' ':
			case ',':
				// Check if inputValue already exists in the array
				if (validateEmailAddress(inputValue) && !value.some(item => item.value === inputValue)) {
					// If not, add it to the array
					setValue(prev => [...prev, { value: inputValue, label: inputValue }]);
					setFormDetails(prev => {
						return { ...prev, emailIds: [...value.map(val => val.value), inputValue] };
					});
				}
				if (!validateEmailAddress(inputValue)) setEmailErrorMessage('Please enter a valid email address');
				else setEmailErrorMessage('');
				setInputValue('');
				break;
		}
	};

	return (
		<form>
			<div className="mb-4 block whiteBgSelect">
				<div className="mb-[4px]">
					<Label>{'Add recipient email '}</Label>
					<span className="text-[red]">*</span>
				</div>
				<div className="text-[10px] mb-[4px]">(Enter email separated by comma or space after each email id.)</div>

				<CreatableSelect
					components={{
						DropdownIndicator: null,
					}}
					inputValue={inputValue}
					isClearable
					isMulti
					menuIsOpen={false}
					onInputChange={newValue => {
						setInputValue(newValue);
						handleInputChange(newValue);
					}}
					onBlur={() => {
						if (inputValue) {
							handleInputChange(' ');
						}
					}}
					onChange={(vals: MultiValue<Dropdown>) => {
						setValue(vals);
						setFormDetails(prev => {
							return { ...prev, emailIds: vals.map(val => val.value) };
						});
					}}
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="teammate-name@company-name.com"
					value={value}
				/>

				{emailErrorMessage ? <div className="mt-2 text-sm text-red-500">{emailErrorMessage}</div> : null}
			</div>

			<div className="block">
				<div className="mb-[8px] flex justify-between items-end">
					<div>
						<Label>{'Your message '}</Label>
					</div>

					<span className="italic text-xs text-[#6B7280] ">{(formDetails?.message?.length || 0) + ' / ' + 640}</span>
				</div>
				<Textarea
					name="message"
					maxLength={640}
					placeholder="Write message here ..."
					onChange={e => {
						setFormDetails(prev => {
							return { ...prev, message: e.target.value };
						});
					}}
					className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[100px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
				/>
			</div>

			<Button
				color="purplePlaced"
				onClick={() => {
					try {
						const shareData = {
							...formDetails,
							userIds: bulkSelectedCandidates.map(candidate => candidate?.user?._id),
							positionId: positionId || '',
						};
						bulkActionFn.mutate(shareData);
					} catch (err) {
						console.log('err:', err);
						toast({
							title: `Something went wrong! ${err}`,
							type: 'error',
						});
					}
				}}
				className="mt-[20px] w-full bg-[#7777E7]"
				disabled={!(formDetails?.emailIds && formDetails?.emailIds?.length > 0) || bulkActionFn.isLoading}
			>
				{bulkActionFn.isLoading ? <Spinner /> : 'Share Candidates'}
			</Button>
		</form>
	);
};

export default RecruiterShareCandidates;
